import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { ArrowLeft, Trash2, Sparkles, Copy, Send, CheckCircle2, AlertTriangle, History, Link2, ExternalLink, Download, Upload, User, Pencil, RefreshCw, Check, X, MessageCircle, Wand2, Ban, UserX, CalendarPlus, Users } from "lucide-react";
import ShowPrepTab from "./guest/ShowPrepTab";
import { Modal } from "./Guests";
import PromoTab from "./guest/PromoTab";
import EmailsTab from "./guest/EmailsTab";

const CHECKLIST = [
  { label: "Booking confirmed", desc: "Guest added to calendar & dashboard" },
  { label: "Confirmation email sent", desc: "Welcomes guest, confirms date/time" },
  { label: "Intake form sent (2 weeks out)", desc: "Bio, headshot, LinkedIn, 3–4 topics" },
  { label: "Form complete", desc: "All required fields received" },
  { label: "5-day reminder sent (if needed)", desc: "Only if form not returned" },
  { label: "Promo assets generated", desc: "Title, description, social, blurb, swipe" },
  { label: "Join link added", desc: "StreamYard / Zoom room where the guest meets you" },
  { label: "Guest swipe copy & graphic emailed", desc: "Share kit sent before air date" },
  { label: "Episode files uploaded", desc: "Dropbox folder with the video, audio MP3 and 2–3 short clips" },
  { label: "Episode published", desc: "Primary platform" },
  { label: "Episode files + published link added", desc: "Dropbox folder, published episode, clip links" },
  { label: "Post-episode thank-you email sent", desc: "Auto-filled from Assets tab" },
];

export default function GuestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ configured: false });
  const [linkCopied, setLinkCopied] = useState(false);
  const [rebookOpen, setRebookOpen] = useState(false);
  const [coGuestOpen, setCoGuestOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/guests/${id}`);
    setG(data);
  }, [id]);
  const [show, setShow] = useState(null);
  useEffect(() => {
    load();
    api.get("/email/status").then(r => setEmailStatus(r.data));
    api.get("/shows/me").then(r => setShow(r.data));
  }, [id, load]);

  if (!g) return <div style={{ padding: 24 }}>Loading...</div>;

  const updateField = (field, value) => setG({ ...g, [field]: value });
  const saveField = async (field, value) => {
    setSaving(true);
    const { data } = await api.put(`/guests/${id}`, { [field]: value });
    setG(data); setSaving(false);
  };
  const saveAll = async () => {
    setSaving(true);
    const payload = { ...g };
    delete payload.id; delete payload.show_id; delete payload.stage; delete payload.created_at; delete payload.updated_at;
    const { data } = await api.put(`/guests/${id}`, payload);
    setG(data); setSaving(false);
  };
  const toggle = async (i) => {
    const { data } = await api.post(`/guests/${id}/checklist`, { index: i, value: !(g.checklist || [])[i] });
    setG({ ...g, checklist: data.checklist, stage: data.stage });
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${g.full_name}? This cannot be undone.`)) return;
    await api.delete(`/guests/${id}`);
    nav("/guests");
  };
  const toggleCancel = async () => {
    const nextCancelled = !g.cancelled;
    const msg = nextCancelled
      ? `Cancel ${g.full_name}? All 5 automated emails + the host prep reminder will stop firing for this guest until you un-cancel.`
      : `Un-cancel ${g.full_name}? The automated sequence will resume based on their episode date.`;
    if (!window.confirm(msg)) return;
    const { data } = await api.put(`/guests/${id}`, { cancelled: nextCancelled });
    setG({ ...g, cancelled: data.cancelled, cancelled_at: data.cancelled_at });
  };

  const toggleNoShow = async () => {
    const next = !g.no_show;
    const msg = next
      ? `Mark ${g.full_name} as a no-show? Every remaining email for this guest stops immediately, and they're excluded from upcoming counts. You can Rebook them afterwards.`
      : `Clear the no-show on ${g.full_name}? The sequence resumes based on their recording date.`;
    if (!window.confirm(msg)) return;
    const { data } = await api.put(`/guests/${id}`, { no_show: next });
    setG({ ...g, no_show: data.no_show, no_show_at: data.no_show_at });
  };

  const doneCount = (g.checklist || []).filter(Boolean).length;
  const copyIntakeLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/intake/${g.intake_token}`);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1800);
  };

  return (
    <div className="pgm-reveal">
      <Link to="/guests" className="pgm-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 8 }} data-testid="back-to-guests">
        <ArrowLeft size={14} /> Back to guests
      </Link>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, marginTop: 4 }}>
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          {g.headshot_url && (
            <img
              src={g.headshot_url.startsWith("/api") ? `${BACKEND_URL}${g.headshot_url}` : g.headshot_url}
              alt={g.full_name}
              style={{ width: 84, height: 84, objectFit: "cover", border: "2px solid var(--pgm-magenta)", flexShrink: 0 }}
              data-testid="guest-header-headshot"
            />
          )}
          <div>
            <div className="pgm-micro">◆ {g.episode_number || "Episode TBD"} · {g.episode_date || "Date TBD"}</div>
            <h1 className="pgm-display" style={{ fontSize: 42, lineHeight: 1.05 }} data-testid="guest-name-heading">{g.full_name}</h1>
            <div style={{ marginTop: 4, color: "var(--pgm-wine)", opacity: 0.8 }}>{g.title || "—"}</div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <span className={`pgm-badge ${g.stage === "Done" ? "pgm-badge-olive" : "pgm-badge-magenta"}`} data-testid="guest-stage-badge">{g.stage}</span>
              {g.cancelled && (
                <span
                  className="pgm-badge"
                  style={{ background: "var(--pgm-orange)", color: "var(--pgm-cream)" }}
                  data-testid="guest-cancelled-badge"
                >
                  CANCELLED
                </span>
              )}
              {g.no_show && (
                <span
                  className="pgm-badge"
                  style={{ background: "#B3261E", color: "var(--pgm-cream)" }}
                  data-testid="guest-no-show-badge"
                >
                  NO-SHOW
                </span>
              )}
              {g.email_bounced && (
                <span
                  className="pgm-badge"
                  style={{ background: "#B3261E", color: "var(--pgm-cream)" }}
                  title={`"${g.email_bounce_template || "Email"}" never arrived — ${g.email_bounce_reason || "the address rejected it"}. Automated emails are paused until the address is fixed on the Info tab.`}
                  data-testid="guest-email-bouncing-badge"
                >
                  EMAIL BOUNCING
                </span>
              )}
              {g.promo_strength && (
                <span
                  className={`pgm-badge ${g.promo_strength === "Strong" ? "pgm-badge-olive" : g.promo_strength === "Good" ? "pgm-badge-magenta" : "pgm-badge-orange"}`}
                  title={
                    g.promo_strength === "Strong"
                      ? "3+ of the 4 headline story questions answered"
                      : g.promo_strength === "Good"
                      ? "1–2 of the 4 headline story questions answered — the more you add, the better the copy"
                      : "No headline story questions answered yet — AI will rely on bio and links only"
                  }
                  data-testid="guest-promo-strength"
                >
                  Promo strength: {g.promo_strength}
                </span>
              )}
              <span style={{ fontSize: 12, color: "var(--pgm-wine)", opacity: 0.7 }}>{doneCount}/12 steps complete</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {g.intake_token && (
            <button onClick={copyIntakeLink} className="pgm-btn pgm-btn-ghost" data-testid="copy-intake-link">
              <Link2 size={14} /> {linkCopied ? "Copied!" : "Copy intake link"}
            </button>
          )}
          <button
            onClick={toggleCancel}
            className="pgm-btn pgm-btn-ghost"
            style={{ borderColor: g.cancelled ? "var(--pgm-olive)" : "var(--pgm-orange)", color: g.cancelled ? "var(--pgm-olive)" : "var(--pgm-orange)" }}
            data-testid="cancel-guest-btn"
            title={g.cancelled ? "Un-cancel to resume automation" : "Cancel this guest to stop ALL automation"}
          >
            <Ban size={14} /> {g.cancelled ? "Un-cancel" : "Cancel guest"}
          </button>
          <button
            onClick={toggleNoShow}
            className="pgm-btn pgm-btn-ghost"
            style={{ borderColor: g.no_show ? "var(--pgm-olive)" : "#B3261E", color: g.no_show ? "var(--pgm-olive)" : "#B3261E" }}
            data-testid="no-show-btn"
            title={g.no_show ? "Clear the no-show and resume the sequence" : "Record that this guest didn't attend — stops every remaining email"}
          >
            <UserX size={14} /> {g.no_show ? "Clear no-show" : "Did not attend"}
          </button>
          {g.no_show && (
            <button
              onClick={() => setRebookOpen(true)}
              className="pgm-btn"
              data-testid="rebook-btn"
              title="Create a fresh booking carrying their details across"
            >
              <CalendarPlus size={14} /> Rebook
            </button>
          )}
          <button onClick={remove} className="pgm-btn pgm-btn-ghost" style={{ borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} data-testid="delete-guest-btn">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {rebookOpen && (
        <RebookModal
          g={g}
          onClose={() => setRebookOpen(false)}
          onDone={(fresh) => { setRebookOpen(false); nav(`/guests/${fresh.id}`); }}
        />
      )}
      {coGuestOpen && (
        <CoGuestModal
          g={g}
          onClose={() => setCoGuestOpen(false)}
          onDone={(created) => { setCoGuestOpen(false); nav(`/guests/${created.id}`); }}
        />
      )}
      <EpisodeGuestsPanel g={g} onAdd={() => setCoGuestOpen(true)} />

      <div className="pgm-progress" style={{ marginTop: 18 }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={`seg-${i}`} className={`pgm-progress-seg ${(g.checklist || [])[i] ? "done" : ""} ${i < 8 ? "pre" : ""}`} />
        ))}
      </div>

      <div className="pgm-tabs" style={{ marginTop: 24 }}>
        {["info", "checklist", "show-prep", "promo", "emails", "assets"].map(t => (
          <button key={t} className={`pgm-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t.replace("-", " ")}</button>
        ))}
      </div>

      <div style={{ paddingTop: 22 }}>
        {tab === "info" && (
          <InfoTab g={g} setG={setG} onSave={saveAll} saving={saving} show={show} />
        )}
        {tab === "checklist" && (
          <ChecklistTab g={g} onToggle={toggle} />
        )}
        {tab === "show-prep" && (
          <ShowPrepTab guestId={id} showPrep={g.show_prep || {}} onUpdated={(prep) => setG({ ...g, show_prep: prep })} />
        )}
        {tab === "promo" && (
          <PromoTab g={g} onGenerated={(data) => setG({ ...g, ...data })} onKitImages={(imgs) => setG({ ...g, kit_images: imgs })} />
        )}
        {tab === "emails" && (
          <EmailsTab guestId={id} guest={g} emailStatus={emailStatus} show={show} onSent={load} />
        )}
        {tab === "assets" && (
          <AssetsTab g={g} onChange={(assets) => saveField("assets", assets)} />
        )}
      </div>
    </div>
  );
}

function CadenceTimeline({ g, show }) {
  const [now, setNow] = useState(() => new Date());
  const [emailLogs, setEmailLogs] = useState([]);
  useEffect(() => { setNow(new Date()); }, [g.episode_date, g.form_submitted_at, g.broadcast_link]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/guests/${g.id}/email-log`).then(({ data }) => {
      if (!cancelled) setEmailLogs(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancelled) setEmailLogs([]); });
    return () => { cancelled = true; };
  }, [g.id]);

  const episodeDate = g.episode_date ? new Date(`${g.episode_date}T09:00:00Z`) : null;
  const isValid = episodeDate && !isNaN(episodeDate.getTime());
  const dayMs = 24 * 60 * 60 * 1000;
  const daysBetween = (a, b) => Math.floor((a.getTime() - b.getTime()) / dayMs);
  const fmt = (d) => d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

  const sentByKey = React.useMemo(() => {
    const map = {};
    for (const row of emailLogs) {
      if (row.sent && row.template_key && !map[row.template_key]) {
        map[row.template_key] = row.created_at || row.sent_at || null;
      }
    }
    return map;
  }, [emailLogs]);

  const formSubmitted = !!g.form_submitted_at;
  const hasBroadcastLink = !!((g.broadcast_link || "").trim());
  const format = show?.show_format || "live_video";

  // Build the ordered milestone list.
  const bookingConf = {
    key: "booking_confirmation",
    label: "Booking confirmation",
    when: null,  // fires on booking, not tied to episode
    detail: sentByKey.booking_confirmation
      ? `Sent ${fmt(new Date(sentByKey.booking_confirmation))}`
      : "Auto-sent when the guest was booked",
    status: sentByKey.booking_confirmation ? "sent" : "pending",
  };

  const intakeForm = (() => {
    const start = isValid ? new Date(episodeDate.getTime() - 14 * dayMs) : null;
    if (sentByKey.intake_form) return { key: "intake_form", label: "Intake form", when: start, detail: `Sent ${fmt(new Date(sentByKey.intake_form))}`, status: "sent" };
    if (!sentByKey.booking_confirmation) return { key: "intake_form", label: "Intake form", when: start, detail: "Waits until the booking confirmation has gone out", status: "blocked" };
    if (!isValid) return { key: "intake_form", label: "Intake form", when: null, detail: "No episode date yet — cadence won't fire", status: "blocked" };
    const daysOut = daysBetween(episodeDate, now);
    if (daysOut > 14) return { key: "intake_form", label: "Intake form", when: start, detail: `Scheduled for ${fmt(start)} (${daysOut - 14} day${daysOut - 14 === 1 ? "" : "s"} away)`, status: "scheduled" };
    if (daysOut >= 0) return { key: "intake_form", label: "Intake form", when: start, detail: "Inside the 14-day window — fires on the next 10-minute cycle", status: "eligible" };
    return { key: "intake_form", label: "Intake form", when: start, detail: "Episode passed — won't fire", status: "skipped" };
  })();

  const formReminder = (() => {
    const start = isValid ? new Date(episodeDate.getTime() - 7 * dayMs) : null;
    if (sentByKey.form_reminder) return { key: "form_reminder", label: "Form reminder", when: start, detail: `Sent ${fmt(new Date(sentByKey.form_reminder))} · guest hadn't returned intake`, status: "sent" };
    if (formSubmitted) return { key: "form_reminder", label: "Form reminder", when: start, detail: `Not needed — guest submitted intake ${fmt(new Date(g.form_submitted_at))}`, status: "skipped" };
    if (!isValid) return { key: "form_reminder", label: "Form reminder", when: null, detail: "No episode date yet — cadence won't fire", status: "blocked" };
    const daysOut = daysBetween(episodeDate, now);
    if (daysOut > 7) return { key: "form_reminder", label: "Form reminder", when: start, detail: `Scheduled for ${fmt(start)} (${daysOut - 7} day${daysOut - 7 === 1 ? "" : "s"} away)`, status: "scheduled" };
    if (daysOut >= 0) return { key: "form_reminder", label: "Form reminder", when: start, detail: `Will fire on the next 10-minute cycle — form still not returned`, status: "eligible" };
    return { key: "form_reminder", label: "Form reminder", when: start, detail: "Episode passed — reminder won't fire", status: "skipped" };
  })();

  const preShow = (() => {
    if (format === "audio_podcast") {
      return { key: "preshow_checkin", label: "Pre-show check-in", when: null, detail: "Audio podcast — no pre-show email in this format", status: "skipped" };
    }
    const start = isValid ? new Date(episodeDate.getTime() - 4 * dayMs) : null;
    if (sentByKey.preshow_checkin) return { key: "preshow_checkin", label: "Pre-show check-in", when: start, detail: `Sent ${fmt(new Date(sentByKey.preshow_checkin))} · calendar invite updated with the join link`, status: "sent" };
    if (!isValid) return { key: "preshow_checkin", label: "Pre-show check-in", when: null, detail: "No episode date yet — cadence won't fire", status: "blocked" };
    if (!hasBroadcastLink) return { key: "preshow_checkin", label: "Pre-show check-in", when: start, detail: "HELD — no join link on this guest yet. Add the join link on the Info tab to unlock (you'll get an email reminder instead).", status: "blocked" };
    const daysOut = daysBetween(episodeDate, now);
    if (daysOut > 4) return { key: "preshow_checkin", label: "Pre-show check-in", when: start, detail: `Scheduled for ${fmt(start)} (${daysOut - 4} day${daysOut - 4 === 1 ? "" : "s"} away)`, status: "scheduled" };
    if (daysOut >= 0) return { key: "preshow_checkin", label: "Pre-show check-in", when: start, detail: "Will fire on the next 10-minute cycle", status: "eligible" };
    return { key: "preshow_checkin", label: "Pre-show check-in", when: start, detail: "Episode passed — pre-show won't fire", status: "skipped" };
  })();

  const dayOf = (() => {
    if (sentByKey.day_of_reminder) return { key: "day_of_reminder", label: "Day-of reminder", when: episodeDate, detail: `Sent ${fmt(new Date(sentByKey.day_of_reminder))} · 8:00 AM in your timezone`, status: "sent" };
    if (!isValid) return { key: "day_of_reminder", label: "Day-of reminder", when: null, detail: "No episode date yet — cadence won't fire", status: "blocked" };
    if (!hasBroadcastLink) return { key: "day_of_reminder", label: "Day-of reminder", when: episodeDate, detail: "Needs the join link. Without it you get an urgent alert on the morning instead.", status: "blocked" };
    const daysOut = daysBetween(episodeDate, now);
    if (daysOut > 0) return { key: "day_of_reminder", label: "Day-of reminder", when: episodeDate, detail: `Fires 8:00 AM your time on ${fmt(episodeDate)}`, status: "scheduled" };
    if (daysOut === 0) return { key: "day_of_reminder", label: "Day-of reminder", when: episodeDate, detail: "Recording today — fires at/after 8:00 AM your time", status: "eligible" };
    return { key: "day_of_reminder", label: "Day-of reminder", when: episodeDate, detail: "Episode passed", status: "skipped" };
  })();

  const timeline = [bookingConf, intakeForm, formReminder, preShow, dayOf];

  const statusStyle = (status) => {
    if (status === "sent") return { color: "var(--pgm-olive)", label: "SENT" };
    if (status === "scheduled") return { color: "var(--pgm-wine)", label: "SCHEDULED" };
    if (status === "eligible") return { color: "var(--pgm-magenta)", label: "FIRING SOON" };
    if (status === "blocked") return { color: "var(--pgm-orange)", label: "HELD" };
    if (status === "skipped") return { color: "var(--pgm-ink)", label: "SKIPPED" };
    return { color: "var(--pgm-ink)", label: "PENDING" };
  };

  return (
    <div style={{ marginTop: 8, padding: 16, background: "var(--pgm-cream)", borderLeft: "3px solid var(--pgm-wine)" }} data-testid="cadence-timeline">
      <div className="pgm-micro" style={{ marginBottom: 6 }}>◆ Email cadence — what's next</div>
      {!isValid && (
        <p style={{ fontSize: 12, color: "var(--pgm-orange)", margin: "0 0 10px 0" }}>
          Set an episode date above so the timeline can compute send dates.
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {timeline.map((m, i) => {
          const s = statusStyle(m.status);
          return (
            <div key={m.key} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 10, background: "#fff", borderLeft: `3px solid ${s.color}` }} data-testid={`timeline-row-${m.key}`}>
              <div style={{ minWidth: 24, fontFamily: "var(--pgm-serif)", fontSize: 20, color: s.color, lineHeight: 1 }}>{i + 1}.</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--pgm-ink)" }}>{m.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: s.color, padding: "2px 8px", border: `1px solid ${s.color}` }} data-testid={`timeline-status-${m.key}`}>{s.label}</span>
                  {m.when && (
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{fmt(m.when)}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--pgm-ink)", opacity: 0.75, marginTop: 4 }} data-testid={`timeline-detail-${m.key}`}>{m.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 11, opacity: 0.55, marginTop: 10, margin: "10px 0 0 0" }}>
        Scheduler runs every 10 minutes in your show timezone. Emails always go in order 1 → 2 → 3 → 4 → 5, once each, and any email blocked by a missing join link emails you instead.
      </p>
    </div>
  );
}


function EpisodeGuestsPanel({ g, onAdd }) {
  const co = g.co_guests || [];
  return (
    <div
      style={{
        marginTop: 16, padding: "12px 16px", border: "1.5px solid var(--pgm-rule)",
        background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, flexWrap: "wrap",
      }}
      data-testid="episode-guests-panel"
    >
      <div>
        <div className="pgm-micro" style={{ color: "var(--pgm-wine)" }}>
          <Users size={11} style={{ verticalAlign: "-1px" }} /> On this recording
        </div>
        {co.length === 0 ? (
          <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
            Just {g.full_name.split(" ")[0]}. Running a panel? Add a co-guest and they'll share the date, time and join link while keeping their own intake, emails and confirmation.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            {co.map((c) => (
              <Link
                key={c.id}
                to={`/guests/${c.id}`}
                style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid var(--pgm-rule)", background: "var(--pgm-cream)" }}
                data-testid={`co-guest-${c.id}`}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--pgm-wine)" }}>{c.full_name}</span>
                <span
                  className="pgm-badge"
                  style={c.no_show
                    ? { background: "#B3261E", color: "var(--pgm-cream)" }
                    : c.confirmed
                    ? { background: "var(--pgm-olive)", color: "var(--pgm-cream)" }
                    : { background: "transparent", color: "var(--pgm-orange)", border: "1px solid var(--pgm-orange)" }}
                >
                  {c.no_show ? "NO-SHOW" : c.confirmed ? "CONFIRMED" : "NOT CONFIRMED"}
                </span>
                <span style={{ fontSize: 11, opacity: 0.7 }}>{c.form_submitted_at ? "form in" : "form pending"}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <button className="pgm-btn pgm-btn-ghost" onClick={onAdd} data-testid="add-co-guest-btn" style={{ whiteSpace: "nowrap" }}>
        <Users size={14} /> Add co-guest
      </button>
    </div>
  );
}

function CoGuestModal({ g, onClose, onDone }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", whatsapp: "", title: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/guests/${g.id}/co-guest`, form);
      onDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't add the co-guest");
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Add co-guest" width={560}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="co-guest-modal">
        <p style={{ fontSize: 13, opacity: 0.8, lineHeight: 1.5 }}>
          They join {g.episode_date || "this recording"}{g.episode_time ? ` at ${g.episode_time}` : ""} and share the join link and episode promo copy. Their intake form, emails, checklist and confirmation are entirely their own.
        </p>
        <div><label className="pgm-label">Full name</label><input className="pgm-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="co-guest-name" /></div>
        <div><label className="pgm-label">Email</label><input className="pgm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="co-guest-email" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label className="pgm-label">Mobile number</label><input className="pgm-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="co-guest-phone" /></div>
          <div><label className="pgm-label">WhatsApp number</label><input className="pgm-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="co-guest-whatsapp" /></div>
        </div>
        <div><label className="pgm-label">Title / role</label><input className="pgm-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="co-guest-title" /></div>
        {error && <div style={{ color: "var(--pgm-orange)", fontSize: 13 }} data-testid="co-guest-error">{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" className="pgm-btn" disabled={busy} data-testid="co-guest-submit">{busy ? "Adding…" : "Add co-guest"}</button>
          <button type="button" className="pgm-btn pgm-btn-ghost" onClick={onClose} data-testid="co-guest-cancel">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function RebookModal({ g, onClose, onDone }) {
  const [form, setForm] = useState({ episode_date: "", episode_time: g.episode_time || "", episode_number: "", broadcast_link: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/guests/${g.id}/rebook`, form);
      onDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't rebook this guest");
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Rebook ${g.full_name}`} width={560}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="rebook-modal">
        <p style={{ fontSize: 13, opacity: 0.8, lineHeight: 1.5 }}>
          This creates a fresh booking carrying over their name, contact details, bio, links, headshot and topics. The sequence starts again from the booking confirmation. The no-show record stays in your history.
        </p>
        <div><label className="pgm-label">New recording date</label><input className="pgm-input" type="date" required value={form.episode_date} onChange={(e) => setForm({ ...form, episode_date: e.target.value })} data-testid="rebook-date" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label className="pgm-label">Recording time</label><input className="pgm-input" type="time" value={form.episode_time} onChange={(e) => setForm({ ...form, episode_time: e.target.value })} data-testid="rebook-time" /></div>
          <div><label className="pgm-label">Episode number</label><input className="pgm-input" value={form.episode_number} onChange={(e) => setForm({ ...form, episode_number: e.target.value })} data-testid="rebook-number" /></div>
        </div>
        <div>
          <label className="pgm-label">Join link (where your guest meets you to record)</label>
          <input className="pgm-input" value={form.broadcast_link} onChange={(e) => setForm({ ...form, broadcast_link: e.target.value })} placeholder="StreamYard / Zoom room — can be added later" data-testid="rebook-link" />
        </div>
        {error && <div style={{ color: "var(--pgm-orange)", fontSize: 13 }} data-testid="rebook-error">{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" className="pgm-btn" disabled={busy} data-testid="rebook-submit">{busy ? "Rebooking…" : "Rebook & send confirmation"}</button>
          <button type="button" className="pgm-btn pgm-btn-ghost" onClick={onClose} data-testid="rebook-cancel">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function ConfirmationStatus({ g, setG }) {
  const confirmed = !!g.confirmed;
  const when = g.confirmed_at ? new Date(g.confirmed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        padding: "10px 14px", border: `2px solid ${confirmed ? "var(--pgm-olive)" : "var(--pgm-orange)"}`,
        background: confirmed ? "rgba(164,165,16,0.08)" : "rgba(214,110,51,0.08)",
      }}
      data-testid="guest-confirmation-status"
    >
      <div>
        <div className="pgm-micro" style={{ color: confirmed ? "var(--pgm-olive)" : "var(--pgm-orange)" }}>
          ◆ Attendance
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: confirmed ? "var(--pgm-olive)" : "var(--pgm-orange)" }}>
          {confirmed ? `Confirmed ${when}` : "Not confirmed"}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
          {confirmed
            ? "The guest tapped “Yes, I'll be there”."
            : "They get a one-tap confirm button in the pre-show and day-of emails. If they haven't confirmed 24 hours out, you get an email with their phone number."}
        </div>
      </div>
      <button
        type="button"
        className="pgm-btn pgm-btn-ghost"
        onClick={() => setG({ ...g, confirmed: !confirmed })}
        data-testid="toggle-confirmed"
        style={{ whiteSpace: "nowrap" }}
      >
        {confirmed ? "Mark not confirmed" : "Mark confirmed"}
      </button>
    </div>
  );
}


function InfoTab({ g, setG, onSave, saving, show }) {
  const format = show?.show_format || "live_video";
  const dateLabel = format === "live_video" ? "Live date" : "Publish date";
  const isAudio = format === "audio_podcast";

  // Dynamic story questions come from the show. Guest's story_answers dict keys by question id.
  const storyQuestions = (Array.isArray(show?.intake_questions) && show.intake_questions.length > 0)
    ? show.intake_questions
    : [
      { id: "q1", label: show?.intake_q1_label || "A common belief they disagree with" },
      { id: "q2", label: show?.intake_q2_label || "Specific moment / number / before-and-after" },
    ];
  const answers = g.story_answers || {};
  const setAnswer = (qid, text) => {
    const next = { ...(g.story_answers || {}), [qid]: text };
    // Keep legacy fields in sync so old code paths still work
    const patch = { ...g, story_answers: next };
    if (qid === "q1") patch.story_disagree = text;
    if (qid === "q2") patch.story_result = text;
    setG(patch);
  };
  const F = (key, label, multi = false, placeholder = "") => (
    <div>
      <label className="pgm-label">{label}</label>
      {multi ? (
        <textarea className="pgm-textarea" placeholder={placeholder} value={g[key] || ""} onChange={(e) => setG({ ...g, [key]: e.target.value })} data-testid={`info-${key}`} />
      ) : (
        <input className="pgm-input" placeholder={placeholder} value={g[key] || ""} onChange={(e) => setG({ ...g, [key]: e.target.value })} data-testid={`info-${key}`} />
      )}
    </div>
  );

  return (
    <div className="pgm-reveal-stagger" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={{ gridColumn: "1 / -1" }}>
        <HeadshotField g={g} />
      </div>
      {F("full_name", "Full name")}
      {F("email", "Email")}
      {F("phone", "Mobile number (for recording-day contact)", false, "+1 555 123 4567")}
      {F("whatsapp", "WhatsApp number (useful for international guests)", false, "+1 555 123 4567")}
      <div style={{ gridColumn: "1 / -1" }}>
        <ConfirmationStatus g={g} setG={setG} />
      </div>
      {F("title", "Title / role")}
      {F("episode_number", "Episode #", false, "e.g. TSL001")}
      {F("episode_date", dateLabel)}
      <div>
        <label className="pgm-label">Recording time (optional, in your show timezone)</label>
        <input
          className="pgm-input"
          type="time"
          value={g.episode_time || ""}
          onChange={(e) => setG({ ...g, episode_time: e.target.value })}
          data-testid="info-episode_time"
        />
        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
          Used in the day-of reminder subject + body. Leave blank if you'd rather the email just say "today."
        </div>
      </div>
      {!isAudio && (
        <div>
          <label className="pgm-label">Join link (where your guest meets you to record)</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="pgm-input"
              placeholder="StreamYard / Zoom room for this recording — saving auto-checks step 7"
              value={g.broadcast_link || ""}
              onChange={(e) => setG({ ...g, broadcast_link: e.target.value })}
              data-testid="info-broadcast_link"
              style={{ flex: 1 }}
            />
            {(show?.broadcast_link || "").trim() && (
              <button
                type="button"
                className="pgm-btn pgm-btn-ghost"
                onClick={() => setG({ ...g, broadcast_link: (show?.broadcast_link || "").trim() })}
                disabled={(g.broadcast_link || "").trim() === (show?.broadcast_link || "").trim()}
                data-testid="broadcast-use-show-default"
                title="Copy your show-level default broadcast link into this field"
                style={{ whiteSpace: "nowrap", padding: "0 14px", fontSize: 11 }}
              >
                <Copy size={11} /> Use show default
              </button>
            )}
          </div>
          {!((g.broadcast_link || "").trim()) && (
            <p style={{ fontSize: 11, color: "var(--pgm-orange)", marginTop: 4, marginBottom: 0 }}>
              The pre-show check-in and day-of reminder are held until you set this link. {(show?.broadcast_link || "").trim() ? "Tap ‘Use show default’ to fill it in one click." : ""}
            </p>
          )}
          <p style={{ fontSize: 11, opacity: 0.6, marginTop: 4, marginBottom: 0 }}>
            Not the same as the finished episode — the Dropbox folder and published episode link live on the Assets tab.
          </p>
        </div>
      )}
      {F("headshot_url", "Headshot URL (or paste an external link)")}
      <div style={{ gridColumn: "1 / -1" }}>{F("bio", "Bio (intake form)", true, "Short 3rd-person bio")}</div>
      <div style={{ gridColumn: "1 / -1" }}>{F("topics", "Topics / questions to cover", true, "One per line")}</div>
      {F("linkedin_url", "LinkedIn URL")}
      {F("website", "Website")}
      <div style={{ gridColumn: "1 / -1" }}>{F("linkedin_headline", "LinkedIn headline")}</div>
      <div style={{ gridColumn: "1 / -1" }}>{F("linkedin_about", "LinkedIn About section", true, "Paste guest's About — this is the PRIMARY source for promo copy")}</div>
      {F("facebook", "Facebook")}
      {F("instagram", "Instagram")}
      {F("youtube", "YouTube")}
      {F("lead_magnet_title", "Lead magnet title")}
      <div style={{ gridColumn: "1 / -1" }}>{F("lead_magnet_url", "Lead magnet URL")}</div>
      <div style={{ gridColumn: "1 / -1" }}>{F("notes", "Notes", true)}</div>

      <div style={{ gridColumn: "1 / -1" }}>
        <CadenceTimeline g={g} show={show} />
      </div>

      <div style={{ gridColumn: "1 / -1", marginTop: 8, paddingTop: 18, borderTop: "1px solid var(--pgm-rule)" }} data-testid="info-story-section">
        <div className="pgm-micro">◆ Story Material — Guest's own answers</div>
        <h3 className="pgm-display" style={{ fontSize: 22, marginTop: 4, marginBottom: 4 }}>What the AI uses first</h3>
        <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 14px 0" }}>
          Answered by the guest on the intake form. Editable here. These always take priority over AI enrichment below.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
          <div>
            <label className="pgm-label">One-sentence intro (how they introduce themselves)</label>
            <textarea
              className="pgm-textarea"
              value={g.story_one_sentence_intro || ""}
              onChange={(e) => setG({ ...g, story_one_sentence_intro: e.target.value })}
              data-testid="info-story_one_sentence_intro"
            />
          </div>
          {storyQuestions.map(q => (
            <div key={q.id}>
              <label className="pgm-label">{q.label}</label>
              <textarea
                className="pgm-textarea"
                value={answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                data-testid={`info-story-answer-${q.id}`}
              />
            </div>
          ))}
          <div>
            <label className="pgm-label">What YOU want the audience to walk away with (host&rsquo;s editorial call)</label>
            <textarea
              className="pgm-textarea"
              value={g.story_takeaway || ""}
              onChange={(e) => setG({ ...g, story_takeaway: e.target.value })}
              data-testid="info-story_takeaway"
            />
          </div>
        </div>
      </div>

      <div style={{ gridColumn: "1 / -1" }}>
        <EnrichmentSection g={g} setG={setG} />
      </div>

      <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
        <button className="pgm-btn pgm-btn-olive" disabled={saving} onClick={onSave} data-testid="info-save-btn">{saving ? "Saving..." : "Save changes"}</button>
      </div>
    </div>
  );
}

function EnrichmentSection({ g, setG }) {
  const enr = g.enrichment || {};
  const [status, setStatus] = useState(enr.status || "idle");
  const [msg, setMsg] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    setStatus(g.enrichment?.status || "idle");
  }, [g.enrichment?.status]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/guests/${g.id}`);
        setG((prev) => ({ ...prev, enrichment: data.enrichment, promo_strength: data.promo_strength }));
        if ((data.enrichment?.status || "idle") !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
  }, [g.id, setG]);

  useEffect(() => {
    if (status === "running" && !pollRef.current) startPolling();
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status, startPolling]);

  const refresh = async () => {
    setMsg("");
    try {
      await api.post(`/guests/${g.id}/enrich`);
      setStatus("running");
      startPolling();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Refresh failed");
    }
  };

  const updateField = (key, val) => {
    setG({ ...g, enrichment: { ...(g.enrichment || {}), [key]: val } });
  };

  const sources = enr.sources || [];
  return (
    <div style={{ marginTop: 8, paddingTop: 18, borderTop: "1px solid var(--pgm-rule)" }} data-testid="info-enrichment-section">
      <div className="pgm-micro">◆ AI-suggested from the guest&rsquo;s links</div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
        <div>
          <h3 className="pgm-display" style={{ fontSize: 22, margin: 0 }}>Review before generating promo</h3>
          <p style={{ fontSize: 13, opacity: 0.75, margin: "6px 0 0 0" }}>
            Pulled from the guest&rsquo;s public web pages. Never invented. Guest&rsquo;s own answers above always take priority.
          </p>
        </div>
        <button className="pgm-btn pgm-btn-ghost" onClick={refresh} disabled={status === "running"} data-testid="enrich-refresh-btn">
          <RefreshCw size={12} style={{ animation: status === "running" ? "spin 1s linear infinite" : "none" }} />
          {status === "running" ? "Refreshing…" : "Refresh from web"}
        </button>
      </div>
      {msg && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginTop: 10 }}>{msg}</div>}

      {status === "error" && enr.error && (
        <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginTop: 10 }} data-testid="enrich-error">
          Enrichment failed: {enr.error}
        </div>
      )}
      {enr.note && (
        <div className="pgm-callout" style={{ marginTop: 10 }} data-testid="enrich-note">{enr.note}</div>
      )}
      {sources.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }} data-testid="enrich-sources">
          Sources: {sources.map((s, i) => (
            <span key={s.url}>
              {i > 0 && ", "}
              <span style={{ color: s.ok ? "var(--pgm-olive)" : "var(--pgm-orange)" }}>{s.url}</span>
              {!s.ok && s.error && <span style={{ opacity: 0.6 }}> ({s.error})</span>}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
        {[
          { key: "bio", label: "AI-suggested bio (2–3 sentences)", multi: true },
          { key: "intro", label: "AI-suggested one-sentence intro", multi: false },
          { key: "credibility", label: "Credibility markers (credentials, achievements, media)", multi: true },
          { key: "specifics", label: "Specifics (numbers, results, story fragments)", multi: true },
        ].map(f => (
          <div key={f.key}>
            <label className="pgm-label">{f.label}</label>
            {f.multi ? (
              <textarea
                className="pgm-textarea"
                value={enr[f.key] || ""}
                onChange={(e) => updateField(f.key, e.target.value)}
                data-testid={`enrich-${f.key}`}
              />
            ) : (
              <input
                className="pgm-input"
                value={enr[f.key] || ""}
                onChange={(e) => updateField(f.key, e.target.value)}
                data-testid={`enrich-${f.key}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HeadshotField({ g }) {
  const src = g.headshot_url
    ? (g.headshot_url.startsWith("/api") ? `${BACKEND_URL}${g.headshot_url}` : g.headshot_url)
    : null;
  return (
    <div className="pgm-card" data-testid="headshot-card">
      <label className="pgm-label">Headshot</label>
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <div style={{ width: 110, height: 110, border: "1.5px solid var(--pgm-rule)", background: "var(--pgm-cream)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {src
            ? <img src={src} alt={g.full_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid="headshot-preview" />
            : <User size={28} style={{ opacity: 0.35 }} />}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {src ? (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <a href={src} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11, textDecoration: "none" }} data-testid="headshot-view-btn">
                  <ExternalLink size={12} /> View full size
                </a>
                <a href={src} download={`${(g.full_name || "guest").replace(/\s+/g, "_")}_headshot`} className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11, textDecoration: "none" }} data-testid="headshot-download-btn">
                  <Download size={12} /> Download
                </a>
              </div>
              <span style={{ fontSize: 11, opacity: 0.6 }}>Uploaded via the guest intake form (or set below).</span>
            </>
          ) : (
            <span style={{ fontSize: 12, opacity: 0.65 }}>No headshot yet — the guest can upload one via their intake link, or paste a URL below.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistTab({ g, onToggle }) {
  return (
    <div className="pgm-reveal-stagger">
      <div className="pgm-divider"><strong>◆ PRE-SHOW</strong></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CHECKLIST.slice(0, 8).map((c, i) => (
          <ChecklistRow key={`pre-${i}`} index={i} done={(g.checklist || [])[i]} item={c} onToggle={() => onToggle(i)} />
        ))}
      </div>
      <div className="pgm-divider"><strong>◆ POST-SHOW</strong></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CHECKLIST.slice(8).map((c, i) => (
          <ChecklistRow key={`post-${i}`} index={i + 8} done={(g.checklist || [])[i + 8]} item={c} onToggle={() => onToggle(i + 8)} />
        ))}
      </div>
    </div>
  );
}

function ChecklistRow({ index, done, item, onToggle }) {
  return (
    <label className="pgm-card" style={{ borderLeftColor: done ? "var(--pgm-olive)" : "var(--pgm-magenta)", display: "flex", alignItems: "flex-start", gap: 14, cursor: "pointer", padding: "14px 18px" }} data-testid={`checklist-row-${index}`}>
      <input type="checkbox" checked={!!done} onChange={onToggle} className="pgm-check" data-testid={`checklist-${index}`} />
      <div style={{ flex: 1 }}>
        <div className="pgm-display" style={{ fontSize: 18, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1 }}>{index + 1}. {item.label}</div>
        <div style={{ fontSize: 12, color: "var(--pgm-wine)", opacity: 0.7, marginTop: 2 }}>{item.desc}</div>
      </div>
    </label>
  );
}




function AssetsTab({ g, onChange }) {
  const [a, setA] = useState(g.assets || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setA(g.assets || {}); }, [g.id]);

  const update = (k, v) => { const next = { ...a, [k]: v }; setA(next); };
  const save = () => onChange(a);

  const ready = !!a.storage_folder && !!a.episode_link;

  return (
    <div className="pgm-reveal">
      <div className="pgm-callout" style={{ marginBottom: 16 }}>
        <strong>These are the finished-episode links — not the join link.</strong> The join link (StreamYard/Zoom) lives on the Info tab and is where your guest meets you to record. Here you add the Dropbox folder with the video, audio and clips, plus the published episode link. The post-episode thank-you email can't be sent until both are in, so nobody ever gets a thank-you for an episode that doesn't exist. Saving also auto-checks the matching checklist steps.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="pgm-reveal-stagger">
        <div>
          <label className="pgm-label">Where the files live</label>
          <select className="pgm-select" value={a.storage_platform || "Dropbox"} onChange={(e) => update("storage_platform", e.target.value)} data-testid="assets-platform">
            <option>Dropbox</option><option>Google Drive</option><option>OneDrive</option><option>Vimeo</option><option>Cloud Storage</option><option>Other</option>
          </select>
        </div>
        <div></div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Episode files (Dropbox folder — video, audio, clips) <Badge ok={!!a.storage_folder} /></label>
          <input className="pgm-input" value={a.storage_folder || ""} onChange={(e) => update("storage_folder", e.target.value)} placeholder="One shareable folder URL containing the video, the audio MP3 and the short clips" data-testid="assets-folder" />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Published episode link <Badge ok={!!a.episode_link} /></label>
          <input className="pgm-input" value={a.episode_link || ""} onChange={(e) => update("episode_link", e.target.value)} placeholder="Where the finished episode is live — YouTube, Apple, Spotify…" data-testid="assets-episode" />
        </div>
        {[1, 2, 3].map(n => (
          <div key={n}>
            <label className="pgm-label">Clip {n} for the guest <Badge ok={!!a[`clip_${n}`]} /></label>
            <input className="pgm-input" value={a[`clip_${n}`] || ""} onChange={(e) => update(`clip_${n}`, e.target.value)} placeholder="Direct link to a short clip they can share" data-testid={`assets-clip-${n}`} />
          </div>
        ))}
      </div>

      {ready && (
        <div className="pgm-callout" style={{ marginTop: 16, background: "var(--pgm-olive)", color: "var(--pgm-cream)" }} data-testid="assets-ready">
          <CheckCircle2 size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
          <strong>Thank-you email is ready to send.</strong> Head to the Emails tab → "Post-episode thank-you".
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button className="pgm-btn pgm-btn-olive" onClick={save} data-testid="assets-save">Save assets</button>
      </div>
    </div>
  );
}

function Badge({ ok }) {
  return ok
    ? <span className="pgm-badge pgm-badge-olive" style={{ marginLeft: 8 }}>Linked</span>
    : <span className="pgm-badge pgm-badge-orange" style={{ marginLeft: 8 }}>Not added</span>;
}
